import { Instance, types, flow, cast } from 'mobx-state-tree';
import { ApiError } from 'service/error';
import { checkEula } from 'service/eula';
import { rootStore } from 'store/root';
import { LocField } from 'store/localizations/umbraco';
import { sleep } from 'service/utils';
import { CoreApi } from 'service/axios-api';
import { FeedStore } from './feed';
import { ProductCommon } from './product-api';
import { Branch, IBranch } from './branch';

const { nxlApi } = window;
// data from cms (Umbraco)
export interface IProductCms extends Instance<typeof ProductCms> {}
const _ProductCms = types.model('ProductCms', {
    productId: types.maybeNull(types.string),
    name: types.string,
    subhead: LocField,
    publisher: LocField,
    about: LocField,
    isNewGame: types.boolean,
    showInAllGamesPage: types.boolean,
    developer: types.optional(types.string, '', [null, undefined]),
    keepShowingMedia: types.optional(types.boolean, false),

    android: types.maybeNull(types.string),
    xbox: types.maybeNull(types.string),
    steam: types.maybeNull(types.string),
    ios: types.maybeNull(types.string),
    psn: types.maybeNull(types.string),

    // platforms: types.map(types.maybeNull(types.string)),
    enableCustomPlayButton: types.optional(types.boolean, false),
    customPlayButtonLink: LocField,
    customPlayButtonText: LocField,

    contentsCuration: types.optional(types.boolean, false, [null, undefined]),

    allGamesThumbnail: types.optional(types.string, '', [null, undefined]),
    allGamesThumbnailLogo: types.optional(types.string, '', [null, undefined]),
    backgroundImage: types.optional(types.string, '', [null, undefined]),
    mediaThumbnail: types.optional(types.string, '', [null, undefined]),
    gameIcon: types.optional(types.string, '', [null, undefined]),
    gameLogo: types.optional(types.string, '', [null, undefined]),
    navigationGameIcon: types.optional(types.string, '', [null, undefined]), // tray notice icon
    trayMenuIcon: types.optional(types.string, '', [null, undefined]),
    thumbnail: types.optional(types.string, '', [null, undefined]),
    notificationCenterIcon: types.optional(types.string, '', [null, undefined]),
    logo: types.optional(types.string, '', [null, undefined]),
    officialSiteLink: LocField,
    forumsLink: LocField,
});

export const ProductCms = types.compose(
    _ProductCms,
    types.model({
        name: types.identifier,
    })
);

export interface IProductCmsPc extends Instance<typeof ProductCmsPc> {}
export const ProductCmsPc = types
    .compose(
        'ProductCMSPC',
        _ProductCms,
        ProductCommon,
        types.model({
            feed: types.safeReference(FeedStore),
            genre: types.maybeNull(types.string),

            supportLink: LocField,
            termsOfUseLink: LocField,
            optionalLinkName: LocField,
            optionalLinkUrl: LocField,

            facebookLink: types.optional(types.string, '', [null, undefined]),
            twitchLink: types.optional(types.string, '', [null, undefined]),
            youtubeLink: types.optional(types.string, '', [null, undefined]),
            discordLink: types.optional(types.string, '', [null, undefined]),
            twitterLink: types.optional(types.string, '', [null, undefined]),
            instagramLink: types.optional(types.string, '', [null, undefined]),
            tiktokLink: types.optional(types.string, '', [null, undefined]),

            redemptionLink: LocField,
            redemptionDescription: LocField,
            useEULA: types.optional(types.boolean, false, [null, undefined]),
            eulaVersion: types.optional(types.string, '', [null, undefined]),
            eulaContents: LocField,

            minimumSystem: LocField,
            recommendedSystem: LocField,

            sDSEnable: types.optional(types.boolean, false, [null, undefined]),

            state: types.optional(types.string, 'Loading'),

            publicBranch: types.optional(Branch, { branchName: '', manifestUrl: '' }),
        })
    )
    .views((self) => ({
        // rely on init func
        get status(): string {
            if (self.state !== 'Done') return self.state;
            if (self.access?.ipBlocked && self.enableCustomPlayButton) return 'Custom';
            if (!rootStore.regionConfig.isTW && rootStore.regionConfig.isKd(self.productId) && !rootStore.appStore.IsKdAvailable)
                return 'NotAvailable';
            if (!self.access?.isDeveloper && self.access?.ipBlocked) return 'NotAvailable';
            if (!self.access?.isPlayable) return 'Redeem';
            return 'Register';
        },
    }))
    .actions((self) => ({
        init: flow(function* () {
            self.state = 'Loading';
            try {
                if (self.productId !== '2041000') {
                    yield self.fetchAccess();
                }
                self.state = 'Done';
            } catch (error) {
                self.state = 'InitError';
            }
        }),
        fetchPublicBranch: flow(function* () {
            const { data: branch }: { data: IBranch } = yield CoreApi.get(`/game-build/v1/branch/games/${self.productId}/public`);
            // branch.branchName = 'public';
            self.publicBranch = cast(branch);
        }),
        async register() {
            if (!(await checkEula(self.productId))) return;
            try {
                await CoreApi.post('/game-library/v1/library', { productId: self.productId });
                nxlApi.frontEnd.pub('library'); // update library for all windows
                await sleep(1000);
                nxlApi.frontEnd.playGame({ productId: self.productId });
            } catch (e) {
                let code = 10; // general exception
                if (e instanceof ApiError) {
                    code = e.code;
                }
                rootStore.dialogs.error({ title: 'game-registration-failed', message: code });
            }
        },
        async redeem() {
            const access = await self.fetchAccess();
            if (!access.isPlayable) {
                rootStore.dialogs.open('keyRedemption', { productId: self.productId });
            }
        },
    }))
    .actions((self) => ({
        nextAction() {
            switch (self.status) {
                case 'Redeem':
                    self.redeem();
                    break;
                case 'Register':
                    self.register();
                    break;
                default:
                    return false;
            }
            return true;
        },
    }));

export default ProductCmsPc;
